// Frame-Specific Modular Heraldic System for Goodvale
// Each shield frame (Heater, Gothic, Round, Kite, Baroque, Fortress, Diamond, Minimal)
// owns its own strictly tailored, seamlessly fitting wings, toppings, bottoms, and patterns.

export const EMBLEM_COLORS = [
  {
    id: 'gold',
    name: 'Kaiserliches Gold',
    nameEn: 'Imperial Gold',
    tier: 'standard',
    stops: ['#fff0b5', '#d9b568', '#98713b', '#f1d696'],
    previewHex: '#d9b568',
    glow: 'rgba(217, 181, 104, 0.4)'
  },
  {
    id: 'silver',
    name: 'Ewiges Silber',
    nameEn: 'Eternal Silver',
    tier: 'forge',
    stops: ['#ffffff', '#cbd5e1', '#64748b', '#e2e8f0'],
    previewHex: '#cbd5e1',
    glow: 'rgba(203, 213, 225, 0.4)'
  },
  {
    id: 'bronze',
    name: 'Antike Bronze',
    nameEn: 'Ancient Bronze',
    tier: 'forge',
    stops: ['#ffedd5', '#ea580c', '#7c2d12', '#c2410c'],
    previewHex: '#ea580c',
    glow: 'rgba(234, 88, 12, 0.4)'
  },
  {
    id: 'ruby',
    name: 'Karmin-Rubin',
    nameEn: 'Crimson Ruby',
    tier: 'forge',
    stops: ['#ffe4e6', '#f43f5e', '#881337', '#e11d48'],
    previewHex: '#f43f5e',
    glow: 'rgba(244, 63, 94, 0.4)'
  },
  {
    id: 'sapphire',
    name: 'Königs-Saphir',
    nameEn: 'Royal Sapphire',
    tier: 'forge',
    stops: ['#e0f2fe', '#38bdf8', '#0369a1', '#0284c7'],
    previewHex: '#38bdf8',
    glow: 'rgba(56, 189, 248, 0.4)'
  },
  {
    id: 'emerald',
    name: 'Wald-Smaragd',
    nameEn: 'Forest Emerald',
    tier: 'forge',
    stops: ['#d1fae5', '#34d399', '#065f46', '#059669'],
    previewHex: '#34d399',
    glow: 'rgba(52, 211, 153, 0.4)'
  },
  {
    id: 'amethyst',
    name: 'Arkan-Amethyst',
    nameEn: 'Arcane Amethyst',
    tier: 'forge',
    stops: ['#f3e8ff', '#c084fc', '#6b21a8', '#9333ea'],
    previewHex: '#c084fc',
    glow: 'rgba(192, 132, 252, 0.4)'
  },
  {
    id: 'obsidian',
    name: 'Schatten-Obsidian',
    nameEn: 'Shadow Obsidian',
    tier: 'forge',
    stops: ['#f4f4f5', '#71717a', '#27272a', '#52525b'],
    previewHex: '#71717a',
    glow: 'rgba(113, 113, 122, 0.4)'
  },
  {
    id: 'fire',
    name: 'Flammen-Kupfer',
    nameEn: 'Solar Flame',
    tier: 'forge',
    stops: ['#fef9c3', '#f59e0b', '#b45309', '#f97316'],
    previewHex: '#f59e0b',
    glow: 'rgba(245, 158, 11, 0.4)'
  }
];

// The outer left edge at a given height. Side ornaments use the actual shield
// contour as their anchor instead of a fixed x coordinate.
function shieldEdgeX(frameId, y) {
  switch (frameId) {
    case 'heater':
      return y <= 54 ? 22 : 22 + 28 * Math.pow((y - 54) / 34, 1.7);
    case 'round':
      return 50 - Math.sqrt(Math.max(0, 32 ** 2 - (y - 55) ** 2));
    case 'diamond':
      return 22 + 28 * Math.abs(y - 55) / 33;
    case 'kite':
      return y <= 48 ? 22 + 28 * Math.pow(Math.max(0, 48 - y) / 26, 2) : 22 + 28 * Math.pow((y - 48) / 44, 1.55);
    case 'gothic':
    case 'fortress':
      return y <= 48 ? 22 : 22 + 28 * Math.pow((y - 48) / 40, 1.7);
    case 'baroque':
      return y <= 54 ? 24 : 24 + 26 * Math.pow((y - 54) / 34, 1.5);
    default:
      return y <= 54 ? 22 + 28 * Math.pow(Math.max(0, 22 - y) / 32, 2) : 22 + 28 * Math.pow((y - 54) / 34, 1.7);
  }
}

function attachedLaurel(frameId, heights) {
  return heights.map(y => {
    const x = Number((shieldEdgeX(frameId, y) + 0.6).toFixed(1));
    return `<path d="M${x} ${y}q-10-3-9-11q10 1 9 11Z M${100 - x} ${y}q10-3 9-11q-10 1-9 11Z"/>`;
  }).join('');
}

function wingRoot(frameId) {
  const edge = Number((shieldEdgeX(frameId, 53) + 1).toFixed(1));
  return `<path d="M17 49 Q21 48 ${edge} 51 L${edge} 57 Q21 56 17 55Z M83 49 Q79 48 ${100 - edge} 51 L${100 - edge} 57 Q79 56 83 55Z"/>`;
}

function bottomMount(frameId, bottomId, bottomY) {
  if (bottomId === 'none' || bottomId === 'diamond' || bottomId === 'roots') return '';
  if (bottomId === 'ribbon') {
    const endY = frameId === 'round' ? 100 : 99;
    return `<path d="M47 ${bottomY - 1}H53V${endY}H47Z"/>`;
  }
  const endY = bottomId === 'spikes' ? 98 : bottomId === 'triple_gem' ? 93 : 92;
  return `<path d="M49 ${bottomY - 1}H51V${endY}H49Z"/>`;
}

function toppingMount(frameId, toppingId, topY) {
  if (toppingId === 'star') return `<path d="M49 17H51V${topY + 2}H49Z"/>`;
  if (frameId === 'round' && ['horns', 'diadem'].includes(toppingId)) {
    return `<path d="M49 20H51V${topY + 3}H49Z"/>`;
  }
  return '';
}

function crownMarkup(frameId) {
  const seat = {
    heater: 'M34 27 L50 22 L66 27',
    round: 'M34 28 Q50 18 66 28',
    diamond: 'M42 31 L50 22 L58 31',
    minimal: 'M34 27 Q50 18 66 27',
    baroque: 'M34 22 Q50 22 66 22',
  }[frameId] || 'M34 22 H66';
  const upper = frameId === 'diamond'
    ? 'L63 13 L56 19 L50 7 L44 19 L37 13 Z'
    : `L69 ${frameId === 'round' ? 13 : 10} L59 ${frameId === 'round' ? 19 : 16} L50 ${frameId === 'round' ? 8 : 6} L41 ${frameId === 'round' ? 19 : 16} L31 ${frameId === 'round' ? 13 : 10} Z`;
  const tips = frameId === 'diamond' ? [37, 50, 63] : [31, 50, 69];
  const sideY = frameId === 'diamond' || frameId === 'round' ? 12 : 9;
  const middleY = frameId === 'diamond' ? 6 : frameId === 'round' ? 7 : 5;
  return `<path d="${seat} ${upper}"/><circle cx="${tips[0]}" cy="${sideY}" r="1.3"/><circle cx="50" cy="${middleY}" r="1.8"/><circle cx="${tips[2]}" cy="${sideY}" r="1.3"/>`;
}

// FRAME DEFINITIONS WITH THEIR DEDICATED, SEAMLESS ELEMENTS
export const EMBLEM_FRAMES = [
  // 1. KLASSISCHER WAPPENSCHILD (HEATER) - Basis & Allrounder
  {
    id: 'heater',
    name: 'Klassischer Schild',
    nameEn: 'Classic Heater',
    tier: 'standard',
    topY: 22,
    bottomY: 88,
    iconCenter: [50, 52],
    iconScale: 1.0,
    svgMarkup: `
      <path d="M50 22 L78 31 V54 C78 71 65 82 50 88 C35 82 22 71 22 54 V31 Z" stroke-width="1.8"/>
      <path d="M50 27 L73 34 V54 C73 69 61 78 50 83 C39 78 27 69 27 54 V34 Z" stroke-width="0.6"/>
    `,
    wings: [
      {
        id: 'laurel',
        name: 'Lorbeerkranz',
        nameEn: 'Laurel Wreath',
        tier: 'standard',
        render: () => attachedLaurel('heater', [35, 45, 55, 65, 75])
      },
      {
        id: 'angel_wings',
        name: 'Erhabene Schwingen',
        nameEn: 'Angelic Wings',
        tier: 'forge',
        render: () => `
          <g stroke="none">
            <path d="M19 28 C12 22 4 25 2 34 C6 35 11 33 16 38 C9 38 5 43 4 50 C9 50 13 47 16 52 C10 54 7 60 8 66 C13 63 16 58 18 64 C17 69 17 74 20 78 C20 71 21 64 21 54 Z" />
            <path d="M81 28 C88 22 96 25 98 34 C94 35 89 33 84 38 C91 38 95 43 96 50 C91 50 87 47 84 52 C90 54 93 60 92 66 C87 63 84 58 82 64 C83 69 83 74 80 78 C80 71 79 64 79 54 Z" />
          </g>
        `
      },
      {
        id: 'sunburst',
        name: 'Sonnenkranz',
        nameEn: 'Sunburst Rays',
        tier: 'forge',
        render: () => `
          <g stroke-width="1.1" fill="none">
            <path d="M25 30 L8 21 M22 39 L4 37 M22 53 L2 55 M25 67 L5 71 M34 78 L11 85" />
            <path d="M75 30 L92 21 M78 39 L96 37 M78 53 L98 55 M75 67 L95 71 M66 78 L89 85" />
          </g>
        `
      },
      { id: 'none', name: 'Ohne Flanken', nameEn: 'None', tier: 'standard', render: () => '' }
    ],
    toppings: [
      {
        id: 'crown',
        name: 'Kaiserliche Krone',
        nameEn: 'Imperial Crown',
        tier: 'standard',
        render: () => crownMarkup('heater')
      },
      {
        id: 'helm',
        name: 'Ritterhelm & Visier',
        nameEn: "Knight's Helm",
        tier: 'forge',
        render: () => `
          <path d="M40 22 C37 14 37 8 50 8 C63 8 63 14 60 22 Z"/>
          <path d="M43 14 H57 M45 17 H55" stroke-width="1.1" fill="none"/>
          <path d="M50 8 Q50 3 56 1 Q53 6 50 8 M50 8 Q47 3 44 1 Q47 6 50 8"/>
        `
      },
      {
        id: 'star',
        name: 'Astralstern / Tiara',
        nameEn: 'Celestial Star',
        tier: 'forge',
        render: () => `
          <path d="M50 4 L52 11 L59 11 L53 15 L55 22 L50 18 L45 22 L47 15 L41 11 L48 11 Z"/>
          <circle cx="35" cy="18" r="1.5"/><circle cx="65" cy="18" r="1.5"/>
        `
      },
      {
        id: 'flame',
        name: 'Ewige Flamme',
        nameEn: 'Rising Flame',
        tier: 'forge',
        render: () => `
          <path d="M40 22 C36 14 44 10 50 4 C56 10 64 14 60 22 C56 18 53 17 50 20 C47 17 44 18 40 22 Z"/>
          <path d="M38 12 C41 15 42 18 39 21 C37 18 36 15 38 12 Z"/>
          <path d="M62 12 C59 15 58 18 61 21 C63 18 64 15 62 12 Z"/>
        `
      },
      { id: 'none', name: 'Ohne Helmzier', nameEn: 'None', tier: 'standard', render: () => '' }
    ],
    bottoms: [
      {
        id: 'diamond',
        name: 'Heraldischer Diamant',
        nameEn: 'Crest Diamond',
        tier: 'standard',
        render: () => `<path d="M50 88 V91 M50 91 L55 98 L50 106 L45 98 Z"/>`
      },
      {
        id: 'ribbon',
        name: 'Gilden-Banner / Schärpe',
        nameEn: 'Guild Banner Scroll',
        tier: 'forge',
        render: () => `
          <path d="M26 93 Q50 101 74 93 L72 99 Q50 107 28 99 Z"/>
          <path d="M26 93 L20 97 L28 99 Z M74 93 L80 97 L72 99 Z"/>
        `
      },
      {
        id: 'tassel',
        name: 'Ehrenmedaille & Quaste',
        nameEn: 'Medallion & Tassel',
        tier: 'forge',
        render: () => `
          <circle cx="50" cy="94" r="3.2" fill="none" stroke-width="1"/>
          <circle cx="50" cy="94" r="1.3"/>
          <path d="M47 97 L45 106 H55 L53 97 Z"/>
        `
      },
      { id: 'none', name: 'Ohne Abschluss', nameEn: 'None', tier: 'standard', render: () => '' }
    ],
    patterns: [
      { id: 'none', name: 'Klar / Transparent', nameEn: 'Clean', tier: 'standard', render: () => '' },
      { id: 'solid', name: 'Dunkler Samt', nameEn: 'Dark Velvet', tier: 'forge', render: () => `<path d="M50 27 L73 34 V54 C73 69 61 78 50 83 C39 78 27 69 27 54 V34 Z" fill="#09090b" fill-opacity="0.55"/>` },
      { id: 'per_pale', name: 'Vertikal Gespalten', nameEn: 'Party per Pale', tier: 'forge', render: () => `
        <path d="M50 27 L27 34 V54 C27 69 39 78 50 83 Z" fill="currentColor" fill-opacity="0.16"/>
        <line x1="50" y1="27" x2="50" y2="83" stroke="currentColor" stroke-width="0.8" stroke-opacity="0.5"/>
      `},
      { id: 'quarterly', name: 'Geviertelt', nameEn: 'Quarterly', tier: 'forge', render: () => `
        <path d="M50 27 L27 34 V55 H50 Z" fill="currentColor" fill-opacity="0.18"/>
        <path d="M50 55 H73 V54 C73 69 61 78 50 83 Z" fill="currentColor" fill-opacity="0.18"/>
        <line x1="50" y1="27" x2="50" y2="83" stroke="currentColor" stroke-width="0.8" stroke-opacity="0.4"/>
        <line x1="27" y1="55" x2="73" y2="55" stroke="currentColor" stroke-width="0.8" stroke-opacity="0.4"/>
      `},
      { id: 'chevron', name: 'Heraldischer Sparren', nameEn: 'Chevron Stripe', tier: 'forge', render: () => `<path d="M27 60 L50 44 L73 60 L73 67 L50 51 L27 67 Z" fill="currentColor" fill-opacity="0.25"/>` }
    ]
  },

  // 2. RUNDSCHILD (ROUND / TARGE) - Komplett kreisangepasste Elemente
  {
    id: 'round',
    name: 'Rundschild & Nieten',
    nameEn: 'Round Targe',
    tier: 'forge',
    topY: 23,
    bottomY: 87,
    iconCenter: [50, 55],
    iconScale: 0.95,
    svgMarkup: `
      <circle cx="50" cy="55" r="32" stroke-width="1.8"/>
      <circle cx="50" cy="55" r="27" stroke-width="0.6"/>
      <circle cx="50" cy="55" r="22" stroke-width="0.5" stroke-dasharray="2 3"/>
      <circle cx="50" cy="25" r="1.3" fill="currentColor"/>
      <circle cx="50" cy="85" r="1.3" fill="currentColor"/>
      <circle cx="20" cy="55" r="1.3" fill="currentColor"/>
      <circle cx="80" cy="55" r="1.3" fill="currentColor"/>
    `,
    wings: [
      {
        id: 'laurel',
        name: 'Runder Lorbeerkranz',
        nameEn: 'Circular Laurel Wreath',
        tier: 'standard',
        render: () => attachedLaurel('round', [29, 39, 49, 59, 69, 79])
      },
      {
        id: 'sunburst',
        name: 'Radialer Sonnenkranz',
        nameEn: 'Radial Sun Halo',
        tier: 'forge',
        render: () => Array.from({length: 12}, (_, i) => {
          const deg = -75 + i * 15;
          if (Math.abs(deg) > 75) return '';
          const rad = (deg * Math.PI) / 180;
          const r1 = 32, r2 = 42;
          const lx1 = 50 - Math.cos(rad) * r1, ly1 = 55 + Math.sin(rad) * r1;
          const lx2 = 50 - Math.cos(rad) * r2, ly2 = 55 + Math.sin(rad) * r2;
          const rx1 = 50 + Math.cos(rad) * r1, ry1 = 55 + Math.sin(rad) * r1;
          const rx2 = 50 + Math.cos(rad) * r2, ry2 = 55 + Math.sin(rad) * r2;
          return `<line x1="${lx1.toFixed(1)}" y1="${ly1.toFixed(1)}" x2="${lx2.toFixed(1)}" y2="${ly2.toFixed(1)}" stroke-width="1.1" fill="none"/>
                  <line x1="${rx1.toFixed(1)}" y1="${ry1.toFixed(1)}" x2="${rx2.toFixed(1)}" y2="${ry2.toFixed(1)}" stroke-width="1.1" fill="none"/>`;
        }).join('')
      },
      {
        id: 'angel_wings',
        name: 'Runde Schwingen',
        nameEn: 'Round Shield Wings',
        tier: 'forge',
        render: () => `
          <g stroke="none">
            <path d="M18 34 C11 28 3 32 1 42 C6 43 11 40 15 46 C8 47 4 53 3 60 C8 60 12 56 15 62 C9 64 6 70 7 76 C12 73 15 67 17 73 C16 78 16 82 19 86 C18 78 18 70 19 58 Z" />
            <path d="M82 34 C89 28 97 32 99 42 C94 43 89 40 85 46 C92 47 96 53 97 60 C92 60 88 56 85 62 C91 64 94 70 93 76 C88 73 85 67 83 73 C84 78 84 82 81 86 C82 78 82 70 81 58 Z" />
          </g>
        `
      },
      { id: 'none', name: 'Ohne Flanken', nameEn: 'None', tier: 'standard', render: () => '' }
    ],
    toppings: [
      {
        id: 'crown',
        name: 'Gewölbte Rundkrone',
        nameEn: 'Curved Round Crown',
        tier: 'standard',
        render: () => crownMarkup('round')
      },
      {
        id: 'horns',
        name: 'Widder- / Drachenhörner',
        nameEn: 'Curved Horns',
        tier: 'forge',
        render: () => `
          <path d="M38 25 C34 16 26 10 20 12 C24 16 31 20 35 25 Z"/>
          <path d="M62 25 C66 16 74 10 80 12 C76 16 69 20 65 25 Z"/>
          <circle cx="50" cy="20" r="2.2"/>
        `
      },
      {
        id: 'diadem',
        name: 'Zartes Kreisdiadem',
        nameEn: 'Curved Diadem',
        tier: 'forge',
        render: () => `
          <path d="M35 25 Q50 16 65 25 Q50 20 35 25 Z"/>
          <circle cx="50" cy="16" r="2"/><circle cx="43" cy="18" r="1.2"/><circle cx="57" cy="18" r="1.2"/>
        `
      },
      { id: 'none', name: 'Ohne Helmzier', nameEn: 'None', tier: 'standard', render: () => '' }
    ],
    bottoms: [
      {
        id: 'ribbon',
        name: 'Rundes Gilden-Banner',
        nameEn: 'Curved Banner Ribbon',
        tier: 'standard',
        render: () => `
          <path d="M22 91 Q50 105 78 91 L76 97 Q50 111 24 97 Z"/>
          <path d="M22 91 L16 95 L24 97 Z M78 91 L84 95 L76 97 Z"/>
        `
      },
      {
        id: 'triple_gem',
        name: 'Dreifacher Edelstein',
        nameEn: 'Triple Gem Pendant',
        tier: 'forge',
        render: () => `
          <circle cx="50" cy="92" r="2.3"/><circle cx="44" cy="98" r="1.6"/><circle cx="56" cy="98" r="1.6"/><circle cx="50" cy="104" r="1.2"/>
        `
      },
      { id: 'none', name: 'Ohne Abschluss', nameEn: 'None', tier: 'standard', render: () => '' }
    ],
    patterns: [
      { id: 'none', name: 'Klar / Transparent', nameEn: 'Clean', tier: 'standard', render: () => '' },
      { id: 'solid', name: 'Dunkler Samt', nameEn: 'Dark Velvet', tier: 'forge', render: () => `<circle cx="50" cy="55" r="27" fill="#09090b" fill-opacity="0.55"/>` },
      { id: 'per_pale', name: 'Halbkreis-Spaltung', nameEn: 'Party per Pale', tier: 'forge', render: () => `
        <path d="M50 28 A27 27 0 0 0 50 82 Z" fill="currentColor" fill-opacity="0.16"/>
        <line x1="50" y1="28" x2="50" y2="82" stroke="currentColor" stroke-width="0.8" stroke-opacity="0.5"/>
      `},
      { id: 'quarterly', name: 'Geviertelter Kreis', nameEn: 'Quarterly', tier: 'forge', render: () => `
        <path d="M50 28 A27 27 0 0 0 23 55 H50 Z" fill="currentColor" fill-opacity="0.18"/>
        <path d="M50 55 H77 A27 27 0 0 1 50 82 Z" fill="currentColor" fill-opacity="0.18"/>
        <line x1="50" y1="28" x2="50" y2="82" stroke="currentColor" stroke-width="0.8" stroke-opacity="0.4"/>
        <line x1="23" y1="55" x2="77" y2="55" stroke="currentColor" stroke-width="0.8" stroke-opacity="0.4"/>
      `}
    ]
  },

  // 3. RAUTENSCHILD (DIAMOND / RHOMBUS) - Geometrisch angepasste 45°-Kanten
  {
    id: 'diamond',
    name: 'Rautenschild / Diamant',
    nameEn: 'Diamond Rhombus',
    tier: 'forge',
    topY: 22,
    bottomY: 88,
    iconCenter: [50, 55],
    iconScale: 0.9,
    svgMarkup: `
      <path d="M50 22 L78 55 L50 88 L22 55 Z" stroke-width="1.8"/>
      <path d="M50 27 L73 55 L50 83 L27 55 Z" stroke-width="0.6"/>
      <path d="M50 22 V88 M22 55 H78" stroke-width="0.4" stroke-dasharray="2 3"/>
    `,
    wings: [
      {
        id: 'laurel',
        name: 'Rauten-Lorbeerkranz',
        nameEn: 'Diamond Laurel',
        tier: 'standard',
        render: () => attachedLaurel('diamond', [35, 46, 57, 68, 79])
      },
      {
        id: 'angel_wings',
        name: 'Geometrische Schwingen',
        nameEn: 'Diamond Wings',
        tier: 'forge',
        render: () => `
          <g stroke="none">
            <path d="M19 28 C12 22 4 25 2 34 C7 35 12 33 16 38 C9 39 4 45 4 52 C9 52 13 49 17 55 C11 58 7 64 9 70 C14 67 17 62 19 68 C17 73 17 78 20 82 C21 72 21 62 22 55 Z" />
            <path d="M81 28 C88 22 96 25 98 34 C93 35 88 33 84 38 C91 39 96 45 96 52 C91 52 87 49 83 55 C89 58 93 64 91 70 C86 67 83 62 81 68 C83 73 83 78 80 82 C79 72 79 62 78 55 Z" />
          </g>
        `
      },
      { id: 'none', name: 'Ohne Flanken', nameEn: 'None', tier: 'standard', render: () => '' }
    ],
    toppings: [
      {
        id: 'crown',
        name: 'V-Spitzenkrone',
        nameEn: 'V-Notched Crown',
        tier: 'standard',
        render: () => crownMarkup('diamond')
      },
      {
        id: 'star',
        name: 'Apex-Astralstern',
        nameEn: 'Apex Star',
        tier: 'forge',
        render: () => `
          <path d="M50 4 L52 11 L59 11 L53 15 L55 22 L50 18 L45 22 L47 15 L41 11 L48 11 Z"/>
          <circle cx="35" cy="18" r="1.5"/><circle cx="65" cy="18" r="1.5"/>
        `
      },
      { id: 'none', name: 'Ohne Helmzier', nameEn: 'None', tier: 'standard', render: () => '' }
    ],
    bottoms: [
      {
        id: 'diamond',
        name: 'Doppel-Diamant',
        nameEn: 'Double Diamond',
        tier: 'standard',
        render: () => `<path d="M50 88 V91 M50 91 L55 98 L50 106 L45 98 Z"/>`
      },
      {
        id: 'spikes',
        name: 'Rautenspeere',
        nameEn: 'Diamond Spikes',
        tier: 'forge',
        render: () => `
          <path d="M42 91 L50 104 L58 91 L50 97 Z"/>
          <line x1="38" y1="94" x2="62" y2="102" stroke-width="1"/>
          <line x1="62" y1="94" x2="38" y2="102" stroke-width="1"/>
        `
      },
      { id: 'none', name: 'Ohne Abschluss', nameEn: 'None', tier: 'standard', render: () => '' }
    ],
    patterns: [
      { id: 'none', name: 'Klar / Transparent', nameEn: 'Clean', tier: 'standard', render: () => '' },
      { id: 'solid', name: 'Dunkler Samt', nameEn: 'Dark Velvet', tier: 'forge', render: () => `<path d="M50 27 L73 55 L50 83 L27 55 Z" fill="#09090b" fill-opacity="0.55"/>` },
      { id: 'per_pale', name: 'Halb-Raute Gespalten', nameEn: 'Party per Pale', tier: 'forge', render: () => `
        <path d="M50 27 L27 55 L50 83 Z" fill="currentColor" fill-opacity="0.16"/>
        <line x1="50" y1="27" x2="50" y2="83" stroke="currentColor" stroke-width="0.8" stroke-opacity="0.5"/>
      `}
    ]
  },

  // 4. NORMANNISCHER MANDELSCHILD (KITE) - Gestreckte Form
  {
    id: 'kite',
    name: 'Mandelschild (Kite)',
    nameEn: 'Norman Kite',
    tier: 'forge',
    topY: 22,
    bottomY: 92,
    iconCenter: [50, 48],
    iconScale: 0.95,
    svgMarkup: `
      <path d="M50 22 C72 22 78 32 78 48 C78 68 60 84 50 92 C40 84 22 68 22 48 C22 32 28 22 50 22 Z" stroke-width="1.8"/>
      <path d="M50 26 C68 26 74 35 74 48 C74 65 58 80 50 86 C42 80 26 65 26 48 C26 35 32 26 50 26 Z" stroke-width="0.6"/>
      <path d="M50 22 V92" stroke-width="0.5" stroke-dasharray="2 3"/>
    `,
    wings: [
      {
        id: 'laurel',
        name: 'Gestreckter Lorbeerkranz',
        nameEn: 'Slender Kite Laurel',
        tier: 'standard',
        render: () => attachedLaurel('kite', [31, 41, 51, 61, 71, 81])
      },
      {
        id: 'angel_wings',
        name: 'Kreuzritter-Schwingen',
        nameEn: 'Crusader Wings',
        tier: 'forge',
        render: () => `
          <g stroke="none">
            <path d="M19 28 C12 22 4 25 2 34 C6 35 11 33 16 38 C9 38 5 43 4 50 C9 50 13 47 16 52 C10 54 7 60 8 66 C13 63 16 58 18 64 C17 69 17 74 20 78 C20 71 21 64 21 54 Z" />
            <path d="M81 28 C88 22 96 25 98 34 C94 35 89 33 84 38 C91 38 95 43 96 50 C91 50 87 47 84 52 C90 54 93 60 92 66 C87 63 84 58 82 64 C83 69 83 74 80 78 C80 71 79 64 79 54 Z" />
          </g>
        `
      },
      { id: 'none', name: 'Ohne Flanken', nameEn: 'None', tier: 'standard', render: () => '' }
    ],
    toppings: [
      {
        id: 'crown',
        name: 'Kreuzritter-Krone',
        nameEn: 'Crusader Crown',
        tier: 'standard',
        render: () => crownMarkup('kite')
      },
      {
        id: 'helm',
        name: 'Normannischer Topfhelm',
        nameEn: 'Norman Great Helm',
        tier: 'forge',
        render: () => `
          <path d="M40 22 C37 14 37 8 50 8 C63 8 63 14 60 22 Z"/>
          <path d="M43 14 H57 M45 17 H55" stroke-width="1.1" fill="none"/>
          <path d="M50 8 Q50 3 56 1 Q53 6 50 8 M50 8 Q47 3 44 1 Q47 6 50 8"/>
        `
      },
      { id: 'none', name: 'Ohne Helmzier', nameEn: 'None', tier: 'standard', render: () => '' }
    ],
    bottoms: [
      {
        id: 'diamond',
        name: 'Langer Diamantspitz',
        nameEn: 'Kite Diamond Tip',
        tier: 'standard',
        render: () => `<path d="M50 92 V95 M50 95 L55 102 L50 110 L45 102 Z"/>`
      },
      {
        id: 'roots',
        name: 'Yggdrasil-Wurzeln',
        nameEn: 'Ancient Roots',
        tier: 'forge',
        render: () => `
          <path d="M50 92 Q40 100 32 106 M50 92 Q60 100 68 106 M50 92 V109 M48 98 Q42 105 44 109 M52 98 Q58 105 56 109" fill="none" stroke-width="1.1" stroke-linecap="round"/>
        `
      },
      { id: 'none', name: 'Ohne Abschluss', nameEn: 'None', tier: 'standard', render: () => '' }
    ],
    patterns: [
      { id: 'none', name: 'Klar / Transparent', nameEn: 'Clean', tier: 'standard', render: () => '' },
      { id: 'solid', name: 'Dunkler Samt', nameEn: 'Dark Velvet', tier: 'forge', render: () => `<path d="M50 26 C68 26 74 35 74 48 C74 65 58 80 50 86 C42 80 26 65 26 48 C26 35 32 26 50 26 Z" fill="#09090b" fill-opacity="0.55"/>` }
    ]
  },

  // 5. GOTISCHER SPITZSCHILD (GOTHIC)
  {
    id: 'gothic',
    name: 'Gotischer Spitzschild',
    nameEn: 'Gothic Point',
    tier: 'forge',
    topY: 22,
    bottomY: 88,
    iconCenter: [50, 52],
    iconScale: 1.0,
    svgMarkup: `
      <path d="M50 22 L78 22 V48 C78 68 62 82 50 88 C38 82 22 68 22 48 V22 Z" stroke-width="1.8"/>
      <path d="M50 26 L74 26 V48 C74 65 60 78 50 84 C40 78 26 65 26 48 V26 Z" stroke-width="0.6"/>
      <path d="M22 22 L27 18 L50 22 L73 18 L78 22" stroke-width="1.1"/>
    `,
    wings: [
      {
        id: 'laurel',
        name: 'Gotische Ranken',
        nameEn: 'Gothic Laurel',
        tier: 'standard',
        render: () => attachedLaurel('gothic', [35, 45, 55, 65, 75])
      },
      {
        id: 'mantling',
        name: 'Akanthus-Helmdecke',
        nameEn: 'Acanthus Mantling',
        tier: 'forge',
        render: () => `
          <g stroke="none">
            <path d="M21 24 C12 22 6 31 11 37 C6 40 5 48 10 52 C5 56 7 65 14 69 C15 62 17 55 19 49 C14 45 15 36 21 32 Z" />
            <path d="M79 24 C88 22 94 31 89 37 C94 40 95 48 90 52 C95 56 93 65 86 69 C85 62 83 55 81 49 C86 45 85 36 79 32 Z" />
          </g>
        `
      },
      { id: 'none', name: 'Ohne Flanken', nameEn: 'None', tier: 'standard', render: () => '' }
    ],
    toppings: [
      {
        id: 'battlement',
        name: 'Burgzinnen / Mauerkrone',
        nameEn: 'Mural Battlement',
        tier: 'standard',
        render: () => `
          <path d="M33 22 V13 H37 V17 H43 V13 H47 V17 H53 V13 H57 V17 H63 V13 H67 V22 Z"/>
          <line x1="35" y1="21" x2="65" y2="21" stroke-width="0.8"/>
        `
      },
      {
        id: 'crown',
        name: 'Gotische Zackenkrone',
        nameEn: 'Gothic Spiked Crown',
        tier: 'forge',
        render: () => crownMarkup('gothic')
      },
      { id: 'none', name: 'Ohne Helmzier', nameEn: 'None', tier: 'standard', render: () => '' }
    ],
    bottoms: [
      {
        id: 'spikes',
        name: 'Gotische Lanzenspitzen',
        nameEn: 'Gothic Spikes',
        tier: 'standard',
        render: () => `
          <path d="M42 91 L50 104 L58 91 L50 97 Z"/>
          <line x1="38" y1="94" x2="62" y2="102" stroke-width="1"/>
          <line x1="62" y1="94" x2="38" y2="102" stroke-width="1"/>
        `
      },
      {
        id: 'diamond',
        name: 'Diamantspitze',
        nameEn: 'Gothic Diamond',
        tier: 'forge',
        render: () => `<path d="M50 88 V91 M50 91 L55 98 L50 106 L45 98 Z"/>`
      },
      { id: 'none', name: 'Ohne Abschluss', nameEn: 'None', tier: 'standard', render: () => '' }
    ],
    patterns: [
      { id: 'none', name: 'Klar / Transparent', nameEn: 'Clean', tier: 'standard', render: () => '' },
      { id: 'solid', name: 'Dunkler Samt', nameEn: 'Dark Velvet', tier: 'forge', render: () => `<path d="M50 26 L74 26 V48 C74 65 60 78 50 84 C40 78 26 65 26 48 V26 Z" fill="#09090b" fill-opacity="0.55"/>` }
    ]
  },

  // 6. FESTUNGSSCHILD (FORTRESS)
  {
    id: 'fortress',
    name: 'Zinnenschild / Festung',
    nameEn: 'Fortress Wall',
    tier: 'forge',
    topY: 22,
    bottomY: 88,
    iconCenter: [50, 52],
    iconScale: 1.0,
    svgMarkup: `
      <path d="M50 22 L76 22 V35 L78 46 C78 66 64 79 50 88 C36 79 22 66 22 46 L24 35 V22 Z" stroke-width="1.8"/>
      <path d="M50 26 L72 26 V35 L74 46 C74 63 61 74 50 83 C39 74 26 63 26 46 L28 35 V26 Z" stroke-width="0.6"/>
      <path d="M33 22 V26 M44 22 V26 M56 22 V26 M67 22 V26" stroke-width="0.8"/>
    `,
    wings: [
      {
        id: 'angel_wings',
        name: 'Panzerschwingen',
        nameEn: 'Armored Wings',
        tier: 'standard',
        render: () => `
          <g stroke="none">
            <path d="M19 28 C12 22 4 25 2 34 C6 35 11 33 16 38 C9 38 5 43 4 50 C9 50 13 47 16 52 C10 54 7 60 8 66 C13 63 16 58 18 64 C17 69 17 74 20 78 C20 71 21 64 21 54 Z" />
            <path d="M81 28 C88 22 96 25 98 34 C94 35 89 33 84 38 C91 38 95 43 96 50 C91 50 87 47 84 52 C90 54 93 60 92 66 C87 63 84 58 82 64 C83 69 83 74 80 78 C80 71 79 64 79 54 Z" />
          </g>
        `
      },
      { id: 'none', name: 'Ohne Flanken', nameEn: 'None', tier: 'standard', render: () => '' }
    ],
    toppings: [
      {
        id: 'battlement',
        name: 'Große Wehrturm-Zinnen',
        nameEn: 'Citadel Battlements',
        tier: 'standard',
        render: () => `
          <path d="M33 22 V13 H37 V17 H43 V13 H47 V17 H53 V13 H57 V17 H63 V13 H67 V22 Z"/>
          <line x1="35" y1="21" x2="65" y2="21" stroke-width="0.8"/>
        `
      },
      {
        id: 'crown',
        name: 'Festungskrone',
        nameEn: 'Fortress Crown',
        tier: 'standard',
        render: () => crownMarkup('fortress')
      },
      { id: 'none', name: 'Ohne Helmzier', nameEn: 'None', tier: 'standard', render: () => '' }
    ],
    bottoms: [
      {
        id: 'spikes',
        name: 'Schwere Wehrsperren',
        nameEn: 'Fortress Spikes',
        tier: 'standard',
        render: () => `
          <path d="M42 91 L50 104 L58 91 L50 97 Z"/>
          <line x1="38" y1="94" x2="62" y2="102" stroke-width="1"/>
          <line x1="62" y1="94" x2="38" y2="102" stroke-width="1"/>
        `
      },
      { id: 'none', name: 'Ohne Abschluss', nameEn: 'None', tier: 'standard', render: () => '' }
    ],
    patterns: [
      { id: 'none', name: 'Klar / Transparent', nameEn: 'Clean', tier: 'standard', render: () => '' }
    ]
  },

  // 7. BAROCKER PRUNKSCHILD (BAROQUE)
  {
    id: 'baroque',
    name: 'Barocker Prunkschild',
    nameEn: 'Baroque Ornate',
    tier: 'forge',
    topY: 22,
    bottomY: 88,
    iconCenter: [50, 52],
    iconScale: 0.95,
    svgMarkup: `
      <path d="M50 22 C62 22 72 18 76 26 C73 34 80 44 76 56 C71 70 62 79 50 88 C38 79 29 70 24 56 C20 44 27 34 24 26 C28 18 38 22 50 22 Z" stroke-width="1.8"/>
      <path d="M50 26 C59 26 67 23 70 30 C68 37 73 45 70 54 C66 65 58 73 50 82 C42 73 34 65 30 54 C27 45 32 37 30 30 C33 23 41 26 50 26 Z" stroke-width="0.6"/>
      <circle cx="50" cy="24" r="1.3" fill="currentColor"/>
    `,
    wings: [
      {
        id: 'mantling',
        name: 'Barockes Rollwerk & Ranken',
        nameEn: 'Baroque Volutes',
        tier: 'standard',
        render: () => `
          <g stroke="none">
            <path d="M21 24 C12 22 6 31 11 37 C6 40 5 48 10 52 C5 56 7 65 14 69 C15 62 17 55 19 49 C14 45 15 36 21 32 Z" />
            <path d="M79 24 C88 22 94 31 89 37 C94 40 95 48 90 52 C95 56 93 65 86 69 C85 62 83 55 81 49 C86 45 85 36 79 32 Z" />
          </g>
        `
      },
      { id: 'none', name: 'Ohne Flanken', nameEn: 'None', tier: 'standard', render: () => '' }
    ],
    toppings: [
      {
        id: 'crown',
        name: 'Barocke Prunkkrone',
        nameEn: 'Baroque Royal Crown',
        tier: 'standard',
        render: () => crownMarkup('baroque')
      },
      { id: 'none', name: 'Ohne Helmzier', nameEn: 'None', tier: 'standard', render: () => '' }
    ],
    bottoms: [
      {
        id: 'tassel',
        name: 'Prunk-Medaillon & Quaste',
        nameEn: 'Ornate Medallion',
        tier: 'standard',
        render: () => `
          <circle cx="50" cy="94" r="3.2" fill="none" stroke-width="1"/>
          <circle cx="50" cy="94" r="1.3"/>
          <path d="M47 97 L45 106 H55 L53 97 Z"/>
        `
      },
      { id: 'none', name: 'Ohne Abschluss', nameEn: 'None', tier: 'standard', render: () => '' }
    ],
    patterns: [
      { id: 'none', name: 'Klar / Transparent', nameEn: 'Clean', tier: 'standard', render: () => '' }
    ]
  },

  // 8. SCHLICHTE DOPPELKONTUR (MINIMAL)
  {
    id: 'minimal',
    name: 'Feine Doppelkontur',
    nameEn: 'Minimal Ring',
    tier: 'forge',
    topY: 22,
    bottomY: 88,
    iconCenter: [50, 52],
    iconScale: 1.0,
    svgMarkup: `
      <path d="M50 22 C72 22 78 34 78 54 C78 72 65 82 50 88 C35 82 22 72 22 54 C22 34 28 22 50 22 Z" stroke-width="1.4"/>
      <path d="M50 26 C68 26 73 36 73 54 C73 69 61 78 50 84 C39 78 27 69 27 54 C27 36 32 26 50 26 Z" stroke-width="0.7"/>
    `,
    wings: [
      {
        id: 'laurel',
        name: 'Minimalistische Ranke',
        nameEn: 'Minimal Garland',
        tier: 'standard',
        render: () => attachedLaurel('minimal', [35, 45, 55, 65, 75])
      },
      { id: 'none', name: 'Ohne Flanken', nameEn: 'None', tier: 'standard', render: () => '' }
    ],
    toppings: [
      {
        id: 'crown',
        name: 'Schlichte Krone',
        nameEn: 'Minimal Crown',
        tier: 'standard',
        render: () => crownMarkup('minimal')
      },
      { id: 'none', name: 'Ohne Helmzier', nameEn: 'None', tier: 'standard', render: () => '' }
    ],
    bottoms: [
      {
        id: 'diamond',
        name: 'Schlichter Diamant',
        nameEn: 'Minimal Diamond',
        tier: 'standard',
        render: () => `<path d="M50 88 V91 M50 91 L55 98 L50 106 L45 98 Z"/>`
      },
      { id: 'none', name: 'Ohne Abschluss', nameEn: 'None', tier: 'standard', render: () => '' }
    ],
    patterns: [
      { id: 'none', name: 'Klar / Transparent', nameEn: 'Clean', tier: 'standard', render: () => '' }
    ]
  }
];

// Frame-aware accessors
export function getWingsForFrame(frameId) {
  const frame = EMBLEM_FRAMES.find(f => f.id === frameId) || EMBLEM_FRAMES[0];
  return frame.wings || [];
}

export function getToppingsForFrame(frameId) {
  const frame = EMBLEM_FRAMES.find(f => f.id === frameId) || EMBLEM_FRAMES[0];
  return frame.toppings || [];
}

export function getBottomsForFrame(frameId) {
  const frame = EMBLEM_FRAMES.find(f => f.id === frameId) || EMBLEM_FRAMES[0];
  return frame.bottoms || [];
}

export function getPatternsForFrame(frameId) {
  const frame = EMBLEM_FRAMES.find(f => f.id === frameId) || EMBLEM_FRAMES[0];
  return frame.patterns || [];
}

// All icons are precisely normalized and optically centered per frame
// All icons are official Lucide React icons precisely normalized and optically centered per frame
export const EMBLEM_ICONS = [
  {
    id: 'none',
    name: 'Ohne Motiv',
    nameEn: 'No motif',
    tier: 'standard',
    category: null,
    pathMarkup: '',
    scale: 1
  },
  // 10 Standard Category Motifs (Lucide React)
  {
    id: 'music',
    name: 'Harfe & Noten',
    nameEn: 'Music & Bard',
    tier: 'standard',
    category: 'ENTERTAINMENT_GASTRO',
    lucideName: 'Music',
    pathMarkup: '<path d="M9 18V5l12-2v13"></path><circle cx="6" cy="18" r="3"></circle><circle cx="18" cy="16" r="3"></circle>',
    scale: 1.3
  },
  {
    id: 'truck',
    name: 'Karawane & Handel',
    nameEn: 'Caravan & Trade',
    tier: 'standard',
    category: 'LOGISTICS_RETAIL',
    lucideName: 'Truck',
    pathMarkup: '<path d="M14 18V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v11a1 1 0 0 0 1 1h2"></path><path d="M15 18H9"></path><path d="M19 18h2a1 1 0 0 0 1-1v-3.65a1 1 0 0 0-.22-.624l-3.48-4.35A1 1 0 0 0 17.52 8H14"></path><circle cx="17" cy="18" r="2"></circle><circle cx="7" cy="18" r="2"></circle>',
    scale: 1.3
  },
  {
    id: 'book',
    name: 'Offenes Buch & Weisheit',
    nameEn: 'Tome of Wisdom',
    tier: 'standard',
    category: 'TECH_EDUCATION',
    lucideName: 'BookOpen',
    pathMarkup: '<path d="M12 7v14"></path><path d="M3 18a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h5a4 4 0 0 1 4 4 4 4 0 0 1 4-4h5a1 1 0 0 1 1 1v13a1 1 0 0 1-1 1h-6a3 3 0 0 0-3 3 3 3 0 0 0-3-3z"></path>',
    scale: 1.3
  },
  {
    id: 'leaf',
    name: 'Lebensblatt & Natur',
    nameEn: 'Leaf of Life',
    tier: 'standard',
    category: 'ECO_NATURE',
    lucideName: 'Leaf',
    pathMarkup: '<path d="M11 20A7 7 0 0 1 9.8 6.1C15.5 5 17 4.48 19 2c1 2 2 4.18 2 8 0 5.5-4.78 10-10 10Z"></path><path d="M2 21c0-3 1.85-5.36 5.08-6C9.5 14.52 12 13 13 12"></path>',
    scale: 1.3
  },
  {
    id: 'heart',
    name: 'Herz & Heilung',
    nameEn: 'Heart of Healing',
    tier: 'standard',
    category: 'HEALTH_WELLNESS',
    lucideName: 'Heart',
    pathMarkup: '<path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z"></path>',
    scale: 1.3
  },
  {
    id: 'compass',
    name: 'Kompass & Pfadfinder',
    nameEn: 'Compass & Wayfinder',
    tier: 'standard',
    category: 'MOBILITY_OUTDOOR',
    lucideName: 'Compass',
    pathMarkup: '<path d="m16.24 7.76-1.804 5.411a2 2 0 0 1-1.265 1.265L7.76 16.24l1.804-5.411a2 2 0 0 1 1.265-1.265z"></path><circle cx="12" cy="12" r="10"></circle>',
    scale: 1.3
  },
  {
    id: 'hammer',
    name: 'Hammer & Meißel',
    nameEn: 'Hammer & Craft',
    tier: 'standard',
    category: 'CRAFTING_REPAIR',
    lucideName: 'Hammer',
    pathMarkup: '<path d="m15 12-8.373 8.373a1 1 0 1 1-3-3L12 9"></path><path d="m18 15 4-4"></path><path d="m21.5 11.5-1.914-1.914A2 2 0 0 1 19 8.172V7l-2.26-2.26a6 6 0 0 0-4.242-1.74H12L2 13l2 2 10-10h.26a6 6 0 0 1 4.242 1.74L20.76 9H22v1.172a2 2 0 0 1-.586 1.414z"></path>',
    scale: 1.3
  },
  {
    id: 'utensils',
    name: 'Kelch & Festmahl',
    nameEn: 'Feast & Chalice',
    tier: 'standard',
    category: 'FOOD_GROCERIES',
    lucideName: 'UtensilsCrossed',
    pathMarkup: '<path d="m16 2-2.3 2.3a3 3 0 0 0 0 4.2l1.8 1.8a3 3 0 0 0 4.2 0L22 8"></path><path d="M15 15 3.3 3.3a4.2 4.2 0 0 0 0 6l7.3 7.3c.7.7 2 .7 2.8 0L15 15Zm0 0 7 7"></path><path d="m2.1 21.8 6.4-6.3"></path><path d="m19 5-7 7"></path>',
    scale: 1.3
  },
  {
    id: 'shield',
    name: 'Wächterschild & Schutz',
    nameEn: 'Guardian Shield',
    tier: 'standard',
    category: 'SOCIAL_COMMUNITY',
    lucideName: 'ShieldCheck',
    pathMarkup: '<path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z"></path><path d="m9 12 2 2 4-4"></path>',
    scale: 1.3
  },
  {
    id: 'tag',
    name: 'Siegel & Gildenband',
    nameEn: 'Guild Seal & Tag',
    tier: 'standard',
    category: null,
    lucideName: 'Tag',
    pathMarkup: '<path d="M12.586 2.586A2 2 0 0 0 11.172 2H4a2 2 0 0 0-2 2v7.172a2 2 0 0 0 .586 1.414l8.704 8.704a2.426 2.426 0 0 0 3.42 0l6.58-6.58a2.426 2.426 0 0 0 0-3.42z"></path><circle cx="7.5" cy="7.5" r=".5" fill="currentColor"></circle>',
    scale: 1.3
  },

  // Wappenschmiede Forge Heraldic Motifs (Lucide React)
  {
    id: 'crossed_swords',
    name: 'Gekreuzte Breitschwerter',
    nameEn: 'Crossed Swords',
    tier: 'forge',
    lucideName: 'Swords',
    pathMarkup: '<polyline points="14.5 17.5 3 6 3 3 6 3 17.5 14.5"></polyline><line x1="13" x2="19" y1="19" y2="13"></line><line x1="16" x2="20" y1="16" y2="20"></line><line x1="19" x2="21" y1="21" y2="19"></line><polyline points="14.5 6.5 18 3 21 3 21 6 17.5 9.5"></polyline><line x1="5" x2="9" y1="14" y2="18"></line><line x1="7" x2="4" y1="17" y2="20"></line><line x1="3" x2="5" y1="19" y2="21"></line>',
    scale: 1.3
  },
  {
    id: 'crown',
    name: 'Kaiserkrone & Hoheit',
    nameEn: 'Imperial Crown',
    tier: 'forge',
    lucideName: 'Crown',
    pathMarkup: '<path d="M11.562 3.266a.5.5 0 0 1 .876 0L15.39 8.87a1 1 0 0 0 1.516.294L21.183 5.5a.5.5 0 0 1 .798.519l-2.834 10.246a1 1 0 0 1-.956.734H5.81a1 1 0 0 1-.957-.734L2.02 6.02a.5.5 0 0 1 .798-.519l4.276 3.664a1 1 0 0 0 1.516-.294z"></path><path d="M5 21h14"></path>',
    scale: 1.3
  },
  {
    id: 'tower',
    name: 'Festung & Wehrturm',
    nameEn: 'Citadel Castle',
    tier: 'forge',
    lucideName: 'Castle',
    pathMarkup: '<path d="M22 20v-9H2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2Z"></path><path d="M18 11V4H6v7"></path><path d="M15 22v-4a3 3 0 0 0-3-3a3 3 0 0 0-3 3v4"></path><path d="M22 11V9"></path><path d="M2 11V9"></path><path d="M6 4V2"></path><path d="M18 4V2"></path><path d="M10 4V2"></path><path d="M14 4V2"></path>',
    scale: 1.3
  },
  {
    id: 'sun',
    name: 'Solarkreuz & Sonnengott',
    nameEn: 'Solar Cross',
    tier: 'forge',
    lucideName: 'Sun',
    pathMarkup: '<circle cx="12" cy="12" r="4"></circle><path d="M12 2v2"></path><path d="M12 20v2"></path><path d="m4.93 4.93 1.41 1.41"></path><path d="m17.66 17.66 1.41 1.41"></path><path d="M2 12h2"></path><path d="M20 12h2"></path><path d="m6.34 17.66-1.41 1.41"></path><path d="m19.07 4.93-1.41 1.41"></path>',
    scale: 1.3
  },
  {
    id: 'potion',
    name: 'Alchemie-Fläschchen',
    nameEn: 'Alchemy Potion',
    tier: 'forge',
    lucideName: 'FlaskConical',
    pathMarkup: '<path d="M14 2v6a2 2 0 0 0 .245.96l5.51 10.08A2 2 0 0 1 18 22H6a2 2 0 0 1-1.755-2.96l5.51-10.08A2 2 0 0 0 10 8V2"></path><path d="M6.453 15h11.094"></path><path d="M8.5 2h7"></path>',
    scale: 1.3
  },
  {
    id: 'tome_staff',
    name: 'Arkaner Stab & Magie',
    nameEn: 'Arcane Staff',
    tier: 'forge',
    lucideName: 'WandSparkles',
    pathMarkup: '<path d="m21.64 3.64-1.28-1.28a1.21 1.21 0 0 0-1.72 0L2.36 18.64a1.21 1.21 0 0 0 0 1.72l1.28 1.28a1.2 1.2 0 0 0 1.72 0L21.64 5.36a1.2 1.2 0 0 0 0-1.72"></path><path d="m14 7 3 3"></path><path d="M5 6v4"></path><path d="M19 14v4"></path><path d="M10 2v2"></path><path d="M7 8H3"></path><path d="M21 16h-4"></path><path d="M11 3H9"></path>',
    scale: 1.3
  },
  {
    id: 'tree_life',
    name: 'Weltenbaum Yggdrasil',
    nameEn: 'Tree of Life',
    tier: 'forge',
    lucideName: 'TreePine',
    pathMarkup: '<path d="m17 14 3 3.3a1 1 0 0 1-.7 1.7H4.7a1 1 0 0 1-.7-1.7L7 14h-.3a1 1 0 0 1-.7-1.7L9 9h-.2A1 1 0 0 1 8 7.3L12 3l4 4.3a1 1 0 0 1-.8 1.7H15l3 3.3a1 1 0 0 1-.7 1.7H17Z"></path><path d="M12 22v-3"></path>',
    scale: 1.3
  },
  {
    id: 'skull',
    name: 'Schattenloge & Schädel',
    nameEn: 'Shadow Skull',
    tier: 'forge',
    lucideName: 'Skull',
    pathMarkup: '<path d="m12.5 17-.5-1-.5 1h1z"></path><path d="M15 22a1 1 0 0 0 1-1v-1a2 2 0 0 0 1.56-3.25 8 8 0 1 0-11.12 0A2 2 0 0 0 8 20v1a1 1 0 0 0 1 1z"></path><circle cx="15" cy="12" r="1"></circle><circle cx="9" cy="12" r="1"></circle>',
    scale: 1.3
  },
  {
    id: 'anchor',
    name: 'Gezeiten-Anker',
    nameEn: 'Tidal Anchor',
    tier: 'forge',
    lucideName: 'Anchor',
    pathMarkup: '<path d="M12 6v16"></path><path d="m19 13 2-1a9 9 0 0 1-18 0l2 1"></path><path d="M9 11h6"></path><circle cx="12" cy="4" r="2"></circle>',
    scale: 1.3
  },
  {
    id: 'axe',
    name: 'Kriegsaxt & Schlacht',
    nameEn: 'Battleaxe',
    tier: 'forge',
    lucideName: 'Axe',
    pathMarkup: '<path d="m14 12-8.381 8.38a1 1 0 0 1-3.001-3L11 9"></path><path d="M15 15.5a.5.5 0 0 0 .5.5A6.5 6.5 0 0 0 22 9.5a.5.5 0 0 0-.5-.5h-1.672a2 2 0 0 1-1.414-.586l-5.062-5.062a1.205 1.205 0 0 0-1.704 0L9.352 5.648a1.205 1.205 0 0 0 0 1.704l5.062 5.062A2 2 0 0 1 15 13.828z"></path>',
    scale: 1.3
  },
  {
    id: 'gem',
    name: 'Strahlender Edelstein',
    nameEn: 'Radiant Gem',
    tier: 'forge',
    lucideName: 'Gem',
    pathMarkup: '<path d="M10.5 3 8 9l4 13 4-13-2.5-6"></path><path d="M17 3a2 2 0 0 1 1.6.8l3 4a2 2 0 0 1 .013 2.382l-7.99 10.986a2 2 0 0 1-3.247 0l-7.99-10.986A2 2 0 0 1 2.4 7.8l2.998-3.997A2 2 0 0 1 7 3z"></path><path d="M2 9h20"></path>',
    scale: 1.3
  },
  {
    id: 'feather_quill',
    name: 'Chronisten-Feder',
    nameEn: 'Chronicle Quill',
    tier: 'forge',
    lucideName: 'Feather',
    pathMarkup: '<path d="M14.086 18.412A2 2 0 0112.67 19H5v-7.672a2 2 0 01.586-1.414L11.75 3.75a6 6 0 118.49 8.49z"></path><path d="M16 8 2 22"></path><path d="M17.488 15H9"></path>',
    scale: 1.3
  },
  {
    id: 'flame_crest',
    name: 'Flammenphönix & Glut',
    nameEn: 'Flame Phoenix',
    tier: 'forge',
    lucideName: 'Flame',
    pathMarkup: '<path d="M12 3q1 4 4 6.5t3 5.5a1 1 0 0 1-14 0 5 5 0 0 1 1-3 1 1 0 0 0 5 0c0-2-1.5-3-1.5-5q0-2 2.5-4"></path>',
    scale: 1.3
  },
  {
    id: 'target',
    name: 'Scharfschütze & Fokus',
    nameEn: 'Wayfinder Target',
    tier: 'forge',
    lucideName: 'Crosshair',
    pathMarkup: '<circle cx="12" cy="12" r="10"></circle><line x1="22" x2="18" y1="12" y2="12"></line><line x1="6" x2="2" y1="12" y2="12"></line><line x1="12" x2="12" y1="6" y2="2"></line><line x1="12" x2="12" y1="22" y2="18"></line>',
    scale: 1.3
  },
  {
    id: 'trophy',
    name: 'Siegestrophäe & Triumph',
    nameEn: 'Trophy of Triumph',
    tier: 'forge',
    lucideName: 'Trophy',
    pathMarkup: '<path d="M10 14.66V17a1 1 0 0 1-1 1 2 2 0 0 0-2 2v2"></path><path d="M14 14.66V17a1 1 0 0 0 1 1 2 2 0 0 1 2 2v2"></path><path d="M17.916 10H19.5A2.5 2.5 0 0 0 22 7.5V5a1 1 0 0 0-1-1h-3"></path><path d="M4 22h16"></path><path d="M6 9a6 6 0 0 0 12 0V3a1 1 0 0 0-1-1H7a1 1 0 0 0-1 1z"></path><path d="M6.084 10H4.5A2.5 2.5 0 0 1 2 7.5V5a1 1 0 0 1 1-1h3"></path>',
    scale: 1.3
  },
  {
    id: 'sparkles',
    name: 'Astral-Sternenglanz',
    nameEn: 'Astral Sparkles',
    tier: 'forge',
    lucideName: 'Sparkles',
    pathMarkup: '<path d="M11.017 2.814a1 1 0 0 1 1.966 0l1.051 5.558a2 2 0 0 0 1.594 1.594l5.558 1.051a1 1 0 0 1 0 1.966l-5.558 1.051a2 2 0 0 0-1.594 1.594l-1.051 5.558a1 1 0 0 1-1.966 0l-1.051-5.558a2 2 0 0 0-1.594-1.594l-5.558-1.051a1 1 0 0 1 0-1.966l5.558-1.051a2 2 0 0 0 1.594-1.594z"></path><path d="M20 2v4"></path><path d="M22 4h-4"></path><circle cx="4" cy="20" r="2"></circle>',
    scale: 1.3
  },
  {
    id: 'key',
    name: 'Meisterschlüssel',
    nameEn: 'Master Key',
    tier: 'forge',
    lucideName: 'Key',
    pathMarkup: '<path d="m15.5 7.5 2.3 2.3a1 1 0 0 0 1.4 0l2.1-2.1a1 1 0 0 0 0-1.4L19 4"></path><path d="m21 2-9.6 9.6"></path><circle cx="7.5" cy="15.5" r="5.5"></circle>',
    scale: 1.3
  },
  {
    id: 'scroll',
    name: 'Uralte Schriftrolle',
    nameEn: 'Ancient Scroll',
    tier: 'forge',
    lucideName: 'Scroll',
    pathMarkup: '<path d="M19 17V5a2 2 0 0 0-2-2H4"></path><path d="M8 21h12a2 2 0 0 0 2-2v-1a1 1 0 0 0-1-1H11a1 1 0 0 0-1 1v1a2 2 0 1 1-4 0V5a2 2 0 1 0-4 0v2a1 1 0 0 0 1 1h3"></path>',
    scale: 1.3
  },
  {
    id: 'eye',
    name: 'Allsehendes Auge',
    nameEn: 'All-Seeing Eye',
    tier: 'forge',
    lucideName: 'Eye',
    pathMarkup: '<path d="M2.062 12.348a1 1 0 0 1 0-.696 10.75 10.75 0 0 1 19.876 0 1 1 0 0 1 0 .696 10.75 10.75 0 0 1-19.876 0"></path><circle cx="12" cy="12" r="3"></circle>',
    scale: 1.3
  },
  {
    id: 'mountain',
    name: 'Ewiger Gipfel',
    nameEn: 'Eternal Peak',
    tier: 'forge',
    lucideName: 'Mountain',
    pathMarkup: '<path d="m8 3 4 8 5-5 5 15H2L8 3z"></path>',
    scale: 1.3
  },
  {
    id: 'moon',
    name: 'Mondsichel & Nacht',
    nameEn: 'Crescent Moon',
    tier: 'forge',
    lucideName: 'Moon',
    pathMarkup: '<path d="M20.985 12.486a9 9 0 1 1-9.473-9.472c.405-.022.617.46.402.803a6 6 0 0 0 8.268 8.268c.344-.215.825-.004.803.401"></path>',
    scale: 1.3
  },
  {
    id: 'coins',
    name: 'Schatzkammer & Münzen',
    nameEn: 'Guild Treasury',
    tier: 'forge',
    lucideName: 'Coins',
    pathMarkup: '<path d="M13.744 17.736a6 6 0 1 1-7.48-7.48"></path><path d="M15 6h1v4"></path><path d="m6.134 14.768.866-.5 2 3.464"></path><circle cx="16" cy="8" r="6"></circle>',
    scale: 1.3
  },
  {
    id: 'pickaxe',
    name: 'Minen-Spitzhacke',
    nameEn: 'Mining Pickaxe',
    tier: 'forge',
    lucideName: 'Pickaxe',
    pathMarkup: '<path d="m14 13-8.381 8.38a1 1 0 0 1-3.001-3L11 9.999"></path><path d="M15.973 4.027A13 13 0 0 0 5.902 2.373c-1.398.342-1.092 2.158.277 2.601a19.9 19.9 0 0 1 5.822 3.024"></path><path d="M16.001 11.999a19.9 19.9 0 0 1 3.024 5.824c.444 1.369 2.26 1.676 2.603.278A13 13 0 0 0 20 8.069"></path><path d="M18.352 3.352a1.205 1.205 0 0 0-1.704 0l-5.296 5.296a1.205 1.205 0 0 0 0 1.704l2.296 2.296a1.205 1.205 0 0 0 1.704 0l5.296-5.296a1.205 1.205 0 0 0 0-1.704z"></path>',
    scale: 1.3
  },
  {
    id: 'zap',
    name: 'Sturmblitz & Kraft',
    nameEn: 'Storm Bolt',
    tier: 'forge',
    lucideName: 'Zap',
    pathMarkup: '<path d="M15.914 4a1.5 1.5 0 00-2.474-1.561l-9 9A1.5 1.5 0 005.5 14h4.002a.5.5 0 01.471.666L8.086 20a1.5 1.5 0 002.475 1.56l9-9A1.5 1.5 0 0018.5 10h-3.997a.5.5 0 01-.472-.667z"></path>',
    scale: 1.3
  },

  // Backward compatibility aliases
  { id: 'dragon', aliasOf: 'flame_crest', name: 'Flammenphönix', nameEn: 'Flame Phoenix', tier: 'forge', lucideName: 'Flame', pathMarkup: '<path d="M12 3q1 4 4 6.5t3 5.5a1 1 0 0 1-14 0 5 5 0 0 1 1-3 1 1 0 0 0 5 0c0-2-1.5-3-1.5-5q0-2 2.5-4"></path>', scale: 1.3 },
  { id: 'lion', aliasOf: 'crown', name: 'Kaiserkrone', nameEn: 'Imperial Crown', tier: 'forge', lucideName: 'Crown', pathMarkup: '<path d="M11.562 3.266a.5.5 0 0 1 .876 0L15.39 8.87a1 1 0 0 0 1.516.294L21.183 5.5a.5.5 0 0 1 .798.519l-2.834 10.246a1 1 0 0 1-.956.734H5.81a1 1 0 0 1-.957-.734L2.02 6.02a.5.5 0 0 1 .798-.519l4.276 3.664a1 1 0 0 0 1.516-.294z"></path><path d="M5 21h14"></path>', scale: 1.3 },
  { id: 'wolf', aliasOf: 'moon', name: 'Mondsichel', nameEn: 'Crescent Moon', tier: 'forge', lucideName: 'Moon', pathMarkup: '<path d="M20.985 12.486a9 9 0 1 1-9.473-9.472c.405-.022.617.46.402.803a6 6 0 0 0 8.268 8.268c.344-.215.825-.004.803.401"></path>', scale: 1.3 },
  { id: 'anvil', aliasOf: 'pickaxe', name: 'Minen-Spitzhacke', nameEn: 'Mining Pickaxe', tier: 'forge', lucideName: 'Pickaxe', pathMarkup: '<path d="m14 13-8.381 8.38a1 1 0 0 1-3.001-3L11 9.999"></path><path d="M15.973 4.027A13 13 0 0 0 5.902 2.373c-1.398.342-1.092 2.158.277 2.601a19.9 19.9 0 0 1 5.822 3.024"></path><path d="M16.001 11.999a19.9 19.9 0 0 1 3.024 5.824c.444 1.369 2.26 1.676 2.603.278A13 13 0 0 0 20 8.069"></path><path d="M18.352 3.352a1.205 1.205 0 0 0-1.704 0l-5.296 5.296a1.205 1.205 0 0 0 0 1.704l2.296 2.296a1.205 1.205 0 0 0 1.704 0l5.296-5.296a1.205 1.205 0 0 0 0-1.704z"></path>', scale: 1.3 }
];

export const DEFAULT_HERALDIC_CONFIG = {
  frame: 'heater',
  wings: 'laurel',
  topping: 'crown',
  bottom: 'diamond',
  icon: 'shield',
  color: 'gold',
  pattern: 'none'
};

export const HERALDIC_EDITABLE_LAYERS = ['icon', 'wings', 'topping', 'bottom', 'pattern'];
export const DEFAULT_HERALDIC_TRANSFORM = { x: 0, y: 0, scale: 1, rotation: 0 };

function normalizeHeraldicTransform(value = {}) {
  const transform = value && typeof value === 'object' ? value : {};
  const bounded = (number, min, max, fallback) => {
    const parsed = Number(number);
    return Number.isFinite(parsed) ? Math.min(max, Math.max(min, parsed)) : fallback;
  };
  return {
    x: bounded(transform.x, -35, 35, 0),
    y: bounded(transform.y, -35, 35, 0),
    scale: bounded(transform.scale, 0.5, 2, 1),
    rotation: bounded(transform.rotation, -180, 180, 0),
  };
}

function layerTransform(transform, centerX, centerY, markup) {
  const { x, y, scale, rotation } = transform;
  return `<g transform="translate(${x} ${y}) translate(${centerX} ${centerY}) rotate(${rotation}) scale(${scale}) translate(${-centerX} ${-centerY})">${markup}</g>`;
}

// Saved crests can contain elements retired from a frame (for example the
// disconnected sword ornament). Resolve them before drawing or showing the
// controls so the selected option always matches the visible crest.
export function normalizeHeraldicConfig(customConfig = {}) {
  const candidate = { ...DEFAULT_HERALDIC_CONFIG, ...customConfig };
  const frame = EMBLEM_FRAMES.find(item => item.id === candidate.frame) || EMBLEM_FRAMES[0];
  const valid = (items, id) => items.some(item => item.id === id) ? id : 'none';
  return {
    frame: frame.id,
    wings: valid(frame.wings, candidate.wings),
    topping: valid(frame.toppings, candidate.topping),
    bottom: valid(frame.bottoms, candidate.bottom),
    pattern: valid(frame.patterns, candidate.pattern),
    icon: EMBLEM_ICONS.some(item => item.id === candidate.icon) ? candidate.icon : DEFAULT_HERALDIC_CONFIG.icon,
    color: EMBLEM_COLORS.some(item => item.id === candidate.color) ? candidate.color : DEFAULT_HERALDIC_CONFIG.color,
    transforms: Object.fromEntries(HERALDIC_EDITABLE_LAYERS.map(layer => [
      layer,
      normalizeHeraldicTransform(candidate.transforms?.[layer]),
    ])),
  };
}

// Builds a clean, fully standalone SVG string with frame-specific element layers
export function generateHeraldicSvg(customConfig = {}) {
  const cfg = normalizeHeraldicConfig(customConfig);

  const colorObj = EMBLEM_COLORS.find(c => c.id === cfg.color) || EMBLEM_COLORS[0];
  const frameObj = EMBLEM_FRAMES.find(f => f.id === cfg.frame) || EMBLEM_FRAMES[0];
  const iconObj = EMBLEM_ICONS.find(i => i.id === cfg.icon) || EMBLEM_ICONS[0];

  // Resolve frame-specific layer elements
  const wingsList = getWingsForFrame(frameObj.id);
  const toppingsList = getToppingsForFrame(frameObj.id);
  const bottomsList = getBottomsForFrame(frameObj.id);
  const patternsList = getPatternsForFrame(frameObj.id);

  const wingsObj = wingsList.find(w => w.id === cfg.wings) || wingsList.find(w => w.id === 'none') || { render: () => '' };
  const toppingObj = toppingsList.find(t => t.id === cfg.topping) || toppingsList[0] || { render: () => '' };
  const bottomObj = bottomsList.find(b => b.id === cfg.bottom) || bottomsList[0] || { render: () => '' };
  const patternObj = patternsList.find(p => p.id === cfg.pattern) || patternsList[0] || { render: () => '' };

  const gradId = `gw-heraldic-grad-${colorObj.id}`;
  const wingMaskId = `gw-heraldic-wing-mask-${frameObj.id}`;
  const outerPath = frameObj.svgMarkup.match(/<path d="([^"]+)"/)?.[1];
  const shieldSilhouette = frameObj.id === 'round'
    ? '<circle cx="50" cy="55" r="32" fill="black" stroke="black" stroke-width="1.5"/>'
    : `<path d="${outerPath}" fill="black" stroke="black" stroke-width="1.5"/>`;

  const gradientDef = `
    <defs>
      <linearGradient id="${gradId}" x1="0%" y1="0%" x2="80%" y2="100%">
        <stop offset="0%" stop-color="${colorObj.stops[0]}"/>
        <stop offset="35%" stop-color="${colorObj.stops[1]}"/>
        <stop offset="65%" stop-color="${colorObj.stops[2]}"/>
        <stop offset="100%" stop-color="${colorObj.stops[3]}"/>
      </linearGradient>
      <mask id="${wingMaskId}" maskUnits="userSpaceOnUse" x="0" y="0" width="100" height="110">
        <rect width="100" height="110" fill="white"/>
        ${shieldSilhouette}
      </mask>
    </defs>
  `;

  // Render elements specifically tailored for this frame
  const wingsMarkup = `${typeof wingsObj.render === 'function' ? wingsObj.render(frameObj.id, frameObj) : ''}${['angel_wings', 'mantling'].includes(wingsObj.id) ? wingRoot(frameObj.id) : ''}`;
  const toppingMarkup = `${toppingMount(frameObj.id, toppingObj.id, frameObj.topY)}${typeof toppingObj.render === 'function' ? toppingObj.render(frameObj.id, frameObj) : ''}`;
  const bottomMarkup = `${bottomMount(frameObj.id, bottomObj.id, frameObj.bottomY)}${typeof bottomObj.render === 'function' ? bottomObj.render(frameObj.id, frameObj) : ''}`;
  const patternMarkup = typeof patternObj.render === 'function' ? patternObj.render(frameObj.id, frameObj) : '';
  const frameMarkup = frameObj.svgMarkup || '';
  const layer = (id, x, y, markup) => layerTransform(cfg.transforms[id], x, y, markup);

  // Icon Motif - centered at frame-specific optical center (standard 24x24 Lucide icon space)
  const [iconX, iconY] = frameObj.iconCenter || [50, 52];
  const frameScale = frameObj.iconScale || 1.0;
  const baseScale = iconObj.scale || 1.3;
  const finalScale = baseScale * frameScale;
  const tx = iconX - 12 * finalScale;
  const ty = iconY - 12 * finalScale;

  const motifMarkup = `
    <g transform="translate(${tx.toFixed(2)} ${ty.toFixed(2)}) scale(${finalScale.toFixed(3)})" stroke="url(#${gradId})" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" fill="none">
      ${iconObj.pathMarkup}
    </g>
  `;

  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 110" data-gw-frame="${cfg.frame}" data-gw-wings="${cfg.wings}" data-gw-topping="${cfg.topping}" data-gw-bottom="${cfg.bottom}" data-gw-icon="${cfg.icon}" data-gw-color="${cfg.color}" data-gw-pattern="${cfg.pattern}" data-gw-transforms="${encodeURIComponent(JSON.stringify(cfg.transforms))}">
  ${gradientDef}
  <!-- Pattern / Background Inlay -->
  <g style="color: ${colorObj.stops[1]}">
    ${layer('pattern', 50, 55, patternMarkup)}
  </g>
  <!-- Side ornaments sit behind the shield and cannot cover its motif. -->
  <g fill="url(#${gradId})" stroke="url(#${gradId})" stroke-linejoin="round" mask="url(#${wingMaskId})">
    ${layer('wings', 50, 55, wingsMarkup)}
  </g>
  <g fill="url(#${gradId})" stroke="url(#${gradId})" stroke-linejoin="round">
    ${layer('topping', 50, frameObj.topY - 10, toppingMarkup)}
    ${layer('bottom', 50, frameObj.bottomY + 9, bottomMarkup)}
  </g>
  <!-- Frame (Stroked in gradient) -->
  <g fill="none" stroke="url(#${gradId})" stroke-linejoin="round">
    ${frameMarkup}
  </g>
  <!-- Center Motif Icon -->
  ${layer('icon', iconX, iconY, motifMarkup)}
</svg>`;

  return svg;
}

// Converts config to standard data URI
export function generateHeraldicDataUri(customConfig = {}) {
  const svg = generateHeraldicSvg(customConfig);
  return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
}

// Inspects an existing URI to extract config or match preset
export function parseHeraldicConfigFromUri(uri) {
  if (!uri || typeof uri !== 'string') return { ...DEFAULT_HERALDIC_CONFIG };

  try {
    const decoded = decodeURIComponent(uri.includes(',') ? uri.split(',').slice(1).join(',') : uri);

    const frameMatch = decoded.match(/data-gw-frame="([^"]+)"/);
    const wingsMatch = decoded.match(/data-gw-wings="([^"]+)"/);
    const toppingMatch = decoded.match(/data-gw-topping="([^"]+)"/);
    const bottomMatch = decoded.match(/data-gw-bottom="([^"]+)"/);
    const iconMatch = decoded.match(/data-gw-icon="([^"]+)"/);
    const colorMatch = decoded.match(/data-gw-color="([^"]+)"/);
    const patternMatch = decoded.match(/data-gw-pattern="([^"]+)"/);
    const transformsMatch = decoded.match(/data-gw-transforms="([^"]+)"/);

    if (frameMatch && iconMatch) {
      return normalizeHeraldicConfig({
        frame: frameMatch[1] || DEFAULT_HERALDIC_CONFIG.frame,
        wings: wingsMatch ? wingsMatch[1] : DEFAULT_HERALDIC_CONFIG.wings,
        topping: toppingMatch ? toppingMatch[1] : DEFAULT_HERALDIC_CONFIG.topping,
        bottom: bottomMatch ? bottomMatch[1] : DEFAULT_HERALDIC_CONFIG.bottom,
        icon: iconMatch[1] || DEFAULT_HERALDIC_CONFIG.icon,
        color: colorMatch ? colorMatch[1] : DEFAULT_HERALDIC_CONFIG.color,
        pattern: patternMatch ? patternMatch[1] : DEFAULT_HERALDIC_CONFIG.pattern,
        transforms: transformsMatch ? JSON.parse(decodeURIComponent(transformsMatch[1])) : undefined,
      });
    }

    if (decoded.includes('M9 18V5l12-2v13') || decoded.includes('lucide-music')) return { ...DEFAULT_HERALDIC_CONFIG, icon: 'music' };
    if (decoded.includes('M14 18V6a2 2') || decoded.includes('rect x="1" y="3"') || decoded.includes('lucide-truck')) return { ...DEFAULT_HERALDIC_CONFIG, icon: 'truck' };
    if (decoded.includes('M12 7v14') || decoded.includes('M2 3h6a4 4 0 0 1 4 4v14') || decoded.includes('lucide-book')) return { ...DEFAULT_HERALDIC_CONFIG, icon: 'book' };
    if (decoded.includes('M11 20A7 7') || decoded.includes('M44 68C31 57') || decoded.includes('M45 66 C33 56') || decoded.includes('lucide-leaf')) return { ...DEFAULT_HERALDIC_CONFIG, icon: 'leaf' };
    if (decoded.includes('M19 14c1.49-1.46') || decoded.includes('lucide-heart')) return { ...DEFAULT_HERALDIC_CONFIG, icon: 'heart' };
    if (decoded.includes('16.24 7.76') || decoded.includes('lucide-compass')) return { ...DEFAULT_HERALDIC_CONFIG, icon: 'compass' };
    if (decoded.includes('m15 12-8.373') || decoded.includes('M34 38l9-9') || decoded.includes('M36 40 L44 32') || decoded.includes('lucide-hammer')) return { ...DEFAULT_HERALDIC_CONFIG, icon: 'hammer' };
    if (decoded.includes('m16 2-2.3') || decoded.includes('M34 29v17') || decoded.includes('M36 32 V46') || decoded.includes('lucide-utensils')) return { ...DEFAULT_HERALDIC_CONFIG, icon: 'utensils' };
    if (decoded.includes('M20 13c0 5') || decoded.includes('M12 22s8-4 8-10V5') || decoded.includes('lucide-shield')) return { ...DEFAULT_HERALDIC_CONFIG, icon: 'shield' };
    if (decoded.includes('12.586 2.586') || decoded.includes('M12 2H2v10l9.29 9.29') || decoded.includes('lucide-tag')) return { ...DEFAULT_HERALDIC_CONFIG, icon: 'tag' };
  } catch (err) {
    console.warn('Error parsing heraldic uri:', err);
  }

  return { ...DEFAULT_HERALDIC_CONFIG };
}
